import classNames from 'classnames'
import type { ChangeEvent, ForwardedRef } from 'react'
import React, {
  forwardRef,
  isValidElement,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import { FormLabel } from './form/FormLabel'
import { FormError } from './form/FormError'
import type { FormFieldDefaults } from './types'
import { useTranslationTyped } from 'i18n/i18n'

export interface BreakoutInputProps extends FormFieldDefaults<'input'> {
  // secondary is the default 'kind' and represents the "primary" style in figma
  // tertiary is the secondary style in figma
  kind?: 'secondary' | 'tertiary'
  sublabel?: string
  // an icon element or a React component that renders an icon
  LeadingIcon?:
    | React.ComponentType<{ size: number } & React.SVGProps<SVGSVGElement>>
    | React.ReactNode
  TrailingIcon?: React.ComponentType<
    { size: number } & React.SVGProps<SVGSVGElement>
  >
  iconClassName?: string
  inputClassName?: string
  allowClear?: boolean
  iconSize?: number
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  preventEditingValue?: boolean
}

export const BreakoutTextInput = forwardRef(function BreakoutTextInput(
  {
    disabled,
    kind = 'tertiary',
    name,
    label,
    labelClass,
    sublabel,
    defaultValue,
    className,
    testId,
    LeadingIcon,
    TrailingIcon,
    iconSize = 18,
    iconClassName,
    required,
    placeholder,
    error,
    errorClass,
    inputClassName,
    allowClear,
    preventEditingValue,
    ...rest
  }: BreakoutInputProps,
  ref: ForwardedRef<HTMLInputElement>
) {
  const { tt } = useTranslationTyped()
  const id = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue || '')

  const canEditValue = !preventEditingValue && !disabled
  const value = rest.value ?? uncontrolledValue

  useEffect(() => {
    const input = inputRef.current
    if (input) {
      const form = input.form
      if (!form) return
      const listener = () => {
        setUncontrolledValue('')
      }
      form.addEventListener('reset', listener)

      return () => {
        form.removeEventListener('reset', listener)
      }
    }
  }, [rest.value])

  return (
    <div className={classNames('flex flex-col items-start gap-1', className)}>
      <FormLabel
        label={label}
        name={name}
        labelClass={labelClass}
        for={id}
        required={required}
      />
      {sublabel && (
        <FormLabel
          label={sublabel}
          name={name}
          labelClass="text-body-small"
          for={id}
        />
      )}

      <div className="relative flex w-full items-center">
        <RenderLeadingIcon
          LeadingIcon={LeadingIcon}
          iconSize={iconSize}
          iconClassName={`${iconClassName || ''} absolute left-3 top-1/2 -translate-y-1/2 transform`}
        />
        <input
          ref={ref || inputRef}
          aria-label={label}
          data-testid={testId || `${name ? name + '-' : ''}input`}
          tabIndex={0}
          name={name}
          id={id}
          disabled={!canEditValue}
          defaultValue={defaultValue || undefined}
          placeholder={placeholder}
          className={classNames(
            'flex h-[3.25rem] w-full flex-row items-center text-label-medium',
            'justify-between rounded-2xl px-4 py-1 outline outline-1 outline-outline-variant focus:text-base md:focus:text-xs',
            inputClassName,
            {
              'bg-core-tertiary': kind === 'tertiary' && canEditValue,
              'bg-core-secondary': kind === 'secondary' && canEditValue,
              'bg-surface-dim': kind === 'secondary' && disabled,
              'bg-surface': kind === 'tertiary' && disabled,
              'text-on-surface-disabled': disabled,
              'pl-9': LeadingIcon,
              'pr-9': TrailingIcon,
              'focus:outline-none focus:ring-2 focus:ring-on-surface-disabled':
                !disabled,
              'border border-core-error focus:ring-1 focus:ring-core-error':
                !!error,
              'cursor-not-allowed': !canEditValue,
            }
          )}
          {...rest}
          onChange={(e) => {
            if (rest.onChange) rest.onChange(e)
            if (rest.value === undefined) {
              setUncontrolledValue(e.target.value)
            }
          }}
        />
        {TrailingIcon && (
          <TrailingIcon
            size={iconSize}
            className={`${iconClassName || ''} absolute right-3 top-1/2 -translate-y-1/2 transform`}
          />
        )}
        {allowClear && value && !disabled && (
          <div
            className="absolute right-3 cursor-pointer p-2"
            data-testid={`datetime-clear-${name}`}
            onClick={() => {
              if (inputRef.current) inputRef.current.value = ''
              const event = { currentTarget: inputRef.current }
              if (rest.value === undefined) {
                setUncontrolledValue('')
              }
              rest.onChange?.({
                target: inputRef.current,
                currentTarget: inputRef.current,
              } as ChangeEvent<HTMLInputElement>)
              rest.onKeyDown?.(event as React.KeyboardEvent<HTMLInputElement>)
              rest.onKeyUp?.(event as React.KeyboardEvent<HTMLInputElement>)
            }}
          >
            <strong className="text-body-medium lowercase text-on-surface-disabled hover:text-core-error">
              {tt.inputs.clear()}
            </strong>
          </div>
        )}
      </div>
      <FormError error={error} errorClass={errorClass} />
    </div>
  )
})

const RenderLeadingIcon = ({
  LeadingIcon,
  iconSize,
  iconClassName,
}: {
  LeadingIcon: BreakoutInputProps['LeadingIcon']
  iconSize: BreakoutInputProps['iconSize']
  iconClassName: BreakoutInputProps['iconClassName']
}) => {
  if (!LeadingIcon) return null

  if (isValidElement(LeadingIcon)) return LeadingIcon

  return (
    // @ts-expect-error - LeadingIcon is a React component
    <LeadingIcon
      size={iconSize}
      className={`${iconClassName || ''} absolute left-3 top-1/2 -translate-y-1/2 transform`}
    />
  )
}
